import { forwardRef, useImperativeHandle, useRef } from "react";
import { useTurnstileWidget, TURNSTILE_SITE_KEY, TURNSTILE_ACTION } from "@/hooks/useTurnstileWidget";

/**
 * Cloudflare Turnstile widget (dark theme to match the site). Purely
 * presentational -- all mount/render/cleanup lifecycle logic lives in
 * `useTurnstileWidget` (see src/hooks/useTurnstileWidget.js). Exposes a
 * clean `onVerify(token)` / `onExpire()` callback pair to the parent
 * form, plus an imperative `reset()` it can call after a submission.
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is not configured, renders nothing --
 * the backend independently treats a missing/unconfigured secret as
 * "Turnstile not required" rather than blocking every submission, so
 * the form still degrades gracefully in that case.
 */
const TurnstileWidget = forwardRef(({ onVerify, onExpire, testId = "turnstile-widget" }, ref) => {
  const containerRef = useRef(null);
  const { error, reset } = useTurnstileWidget({ containerRef, onVerify, onExpire });

  useImperativeHandle(ref, () => ({ reset }));

  if (!TURNSTILE_SITE_KEY) return null;

  return (
    <div className="turnstile-widget-wrap" data-testid={testId}>
      <div ref={containerRef} className="cf-turnstile" data-sitekey={TURNSTILE_SITE_KEY} data-action={TURNSTILE_ACTION} />
      {error && (
        <p className="text-small mt-2" style={{ color: "var(--gg-error, #c96b6b)" }} data-testid={`${testId}-error`}>
          Security verification could not load. Please refresh and try again.
        </p>
      )}
    </div>
  );
});

TurnstileWidget.displayName = "TurnstileWidget";

export default TurnstileWidget;
